import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import type {
  AppOptions,
  PendingActivity,
  PendingConversionInput,
  PendingDealStatus,
  PendingDepositStatus,
  PendingFinanceStatus,
  PendingInput,
  PendingKind,
  PendingRecord,
  PendingScope
} from "../types";

function localDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function addDays(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

const kindLabels: Record<PendingKind, string> = {
  future_sale: "Future Sale",
  call_in: "Call-In",
  both: "Future Sale + Call-In"
};

const dealStatusLabels: Record<PendingDealStatus, string> = {
  working: "Working",
  awaiting_customer: "Awaiting Customer",
  approved: "Approved",
  scheduled: "Scheduled",
  delivered_sold: "Delivered / Sold",
  lost_cancelled: "Lost / Canceled"
};

const financeStatusLabels: Record<PendingFinanceStatus, string> = {
  not_applicable: "Not Applicable",
  needs_call_in: "Needs Call-In",
  submitted: "Submitted to Bank",
  pending_bank: "Pending Bank Decision",
  conditional_approval: "Conditional Approval",
  approved: "Approved",
  stips_needed: "Stipulations Needed",
  declined: "Declined"
};

const depositStatusLabels: Record<PendingDepositStatus, string> = {
  no_deposit: "No Deposit",
  paid_in_full: "Deposit Paid in Full",
  additional_deposit_required: "Additional Deposit Required",
  refund_pending: "Refund Pending",
  refunded: "Refunded",
  applied_to_completed_deal: "Applied to Completed Deal"
};

const scopeLabels: Record<PendingScope, string> = {
  all_open: "All Open",
  today: "Today’s Deliveries",
  tomorrow: "Tomorrow’s Deliveries",
  next_7_days: "Next 7 Days",
  delivery_date: "Choose Delivery Date",
  future: "Future Deals",
  call_ins: "Call-Ins",
  approved: "Approved — Not Delivered",
  deposits_due: "Deposits Due",
  followups_due: "Follow-Ups Due",
  closure_pending: "Lost / Canceled Awaiting Sign-Off",
  closed: "Closed History",
  all: "All Records"
};

const activityLabels: Record<PendingActivity["action"], string> = {
  create: "Created",
  update: "Updated",
  note: "Note Added",
  close: "Lost / Canceled Activity",
  convert: "Converted to Sold"
};

function blankPending(options: AppOptions): PendingInput {
  return {
    kind: "future_sale",
    customer: "",
    vehicle: "",
    stockNumber: "",
    salespersonId: options.salespeople[0]?.id ?? "",
    fiManagerId: options.fiManagers[0]?.id ?? "",
    expectedDate: "",
    deliveryTime: "",
    dealStatus: "working",
    financeStatus: "not_applicable",
    depositStatus: "no_deposit",
    depositAmount: 0,
    depositDue: 0,
    nextFollowUpDate: localDate(),
    note: ""
  };
}

function fromRecord(record: PendingRecord): PendingInput {
  return {
    kind: record.kind,
    customer: record.customer,
    vehicle: record.vehicle,
    stockNumber: record.stockNumber,
    salespersonId: record.salespersonId,
    fiManagerId: record.fiManagerId,
    expectedDate: record.expectedDate,
    deliveryTime: record.deliveryTime,
    dealStatus: record.dealStatus,
    financeStatus: record.financeStatus,
    depositStatus: record.depositStatus,
    depositAmount: record.depositAmount,
    depositDue: record.depositDue,
    nextFollowUpDate: record.nextFollowUpDate,
    note: ""
  };
}

function money(value: number): string {
  return value.toLocaleString("en-US", {style: "currency", currency: "USD"});
}

function shortDate(value: string): string {
  if (!value) return "—";
  return new Date(`${value}T12:00:00`).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
}

function shortTime(value: string): string {
  if (!value) return "";
  const [hourText, minute = "00"] = value.split(":");
  const hour = Number(hourText);
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${suffix}`;
}

export default function PendingTracker({
  options,
  canManage,
  canSignOff,
  initialScope,
  startInAddMode,
  onAddModeOpened
}: {
  options: AppOptions;
  canManage: boolean;
  canSignOff: boolean;
  initialScope: PendingScope;
  startInAddMode: boolean;
  onAddModeOpened: () => void;
}) {
  const [scope, setScope] = useState<PendingScope>(initialScope);
  const [query, setQuery] = useState("");
  const [customDate, setCustomDate] = useState(addDays(localDate(), 1));
  const [records, setRecords] = useState<PendingRecord[]>([]);
  const [selected, setSelected] = useState<PendingRecord | null>(null);
  const [form, setForm] = useState<PendingInput>(() => blankPending(options));
  const [mode, setMode] = useState<"list" | "form" | "history" | "convert" | "signoff">("list");
  const [activity, setActivity] = useState<PendingActivity[]>([]);
  const [signoffNote, setSignoffNote] = useState("");
  const [conversion, setConversion] = useState<PendingConversionInput>({
    date: localDate(),
    timeSlotId: options.timeSlots[0]?.id ?? "09:00",
    department: options.departments[0]?.id ?? "ford",
    typeNotes: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load(nextScope = scope, nextQuery = query, nextCustomDate = customDate) {
    setLoading(true);
    setError("");
    try {
      setRecords(await api.searchPending(nextScope, nextQuery, localDate(), nextScope === "delivery_date" ? nextCustomDate : ""));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Pending deals could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setScope(initialScope);
    void load(initialScope, query, customDate);
  }, [initialScope]);

  useEffect(() => {
    if (startInAddMode && canManage) {
      setSelected(null);
      setForm(blankPending(options));
      setMode("form");
      onAddModeOpened();
    }
  }, [startInAddMode, canManage, onAddModeOpened, options]);

  function openAdd() {
    setSelected(null);
    setForm(blankPending(options));
    setMessage("");
    setError("");
    setMode("form");
  }

  function openEdit(record: PendingRecord) {
    setSelected(record);
    setForm(fromRecord(record));
    setMessage("");
    setError("");
    setMode("form");
  }

  async function openHistory(record: PendingRecord) {
    setSelected(record);
    setMode("history");
    setLoading(true);
    setError("");
    try {
      setActivity(await api.pendingActivity(record.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "History could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  function openConvert(record: PendingRecord) {
    setSelected(record);
    setConversion({
      date: record.expectedDate || localDate(),
      timeSlotId: options.timeSlots[0]?.id ?? "09:00",
      department: options.departments[0]?.id ?? "ford",
      typeNotes: ""
    });
    setMessage("");
    setError("");
    setMode("convert");
  }

  function openSignoff(record: PendingRecord) {
    setSelected(record);
    setSignoffNote("");
    setMessage("");
    setError("");
    setMode("signoff");
  }

  function updateForm<K extends keyof PendingInput>(key: K, value: PendingInput[K]) {
    setForm((current) => {
      const next = {...current, [key]: value};
      if (key === "kind") {
        next.financeStatus = value === "future_sale" ? "not_applicable" : (current.financeStatus === "not_applicable" ? "needs_call_in" : current.financeStatus);
      }
      if (key === "depositStatus") {
        if (value === "no_deposit") {
          next.depositAmount = 0;
          next.depositDue = 0;
        } else if (value !== "additional_deposit_required") {
          next.depositDue = 0;
        }
      }
      if (key === "dealStatus" && value !== "scheduled") {
        next.deliveryTime = "";
      }
      return next;
    });
    setMessage("");
    setError("");
  }

  async function savePending(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage(selected ? "Saving update…" : "Adding pending deal…");
    try {
      if (selected) {
        await api.updatePending(crypto.randomUUID(), selected, form);
        setMessage(form.dealStatus === "lost_cancelled" ? "Lost / canceled request sent for Desk Manager sign-off." : "Pending deal updated.");
      } else {
        await api.createPending(crypto.randomUUID(), form);
        setMessage(form.dealStatus === "lost_cancelled" ? "Lost / canceled request sent for Desk Manager sign-off." : "Pending deal added.");
      }
      setMode("list");
      setSelected(null);
      await load();
    } catch (caught) {
      setMessage("");
      setError(caught instanceof Error ? caught.message : "The pending deal could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function completeSignoff(action: "approve" | "return") {
    if (!selected) return;
    if (action === "return" && !signoffNote.trim()) {
      setError("Enter a note explaining why the deal is being returned to active status.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage(action === "approve" ? "Approving closure…" : "Returning deal to active status…");
    try {
      if (action === "approve") {
        await api.approvePendingClosure(crypto.randomUUID(), selected, signoffNote);
        setMessage("Closure approved and moved to Closed History.");
      } else {
        await api.returnPendingToActive(crypto.randomUUID(), selected, signoffNote);
        setMessage("Deal returned to active status.");
      }
      setMode("list");
      setSelected(null);
      setScope("all_open");
      await load("all_open", query, customDate);
    } catch (caught) {
      setMessage("");
      setError(caught instanceof Error ? caught.message : "The sign-off could not be completed.");
    } finally {
      setSaving(false);
    }
  }

  async function convert(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setSaving(true);
    setError("");
    setMessage("Converting to sold deal…");
    try {
      const result = await api.convertPending(crypto.randomUUID(), selected, conversion);
      setMessage(`Sold deal ${result.deal.dealId} created successfully.`);
      setMode("list");
      setSelected(null);
      await load();
    } catch (caught) {
      setMessage("");
      setError(caught instanceof Error ? caught.message : "The pending deal could not be converted.");
    } finally {
      setSaving(false);
    }
  }

  const visibleFinance = form.kind !== "future_sale";
  const showDepositAmount = form.depositStatus !== "no_deposit";
  const showDepositDue = form.depositStatus === "additional_deposit_required";
  const showDeliveryTime = form.dealStatus === "scheduled";
  const closureRequested = form.dealStatus === "lost_cancelled";
  const openRecords = useMemo(() => records.filter((record) => record.dealStatus !== "delivered_sold" && record.closureStatus !== "closed"), [records]);

  if (mode === "form") {
    return (
      <section className="panel pending-form-panel">
        <div className="panel-heading">
          <div><span className="eyebrow">Simple status tracking</span><h2>{selected ? `Update ${selected.pendingId}` : "Add Pending Deal"}</h2></div>
          <button type="button" className="secondary" onClick={() => setMode("list")}>Back to Tracker</button>
        </div>
        <form className="pending-form" onSubmit={savePending}>
          <label>Tracking Type<select value={form.kind} onChange={(e) => updateForm("kind", e.target.value as PendingKind)}>
            {Object.entries(kindLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select></label>
          <label>Customer<input value={form.customer} onChange={(e) => updateForm("customer", e.target.value)} required maxLength={160} /></label>
          <label>Vehicle<input value={form.vehicle} onChange={(e) => updateForm("vehicle", e.target.value)} required maxLength={160} /></label>
          <label>Stock Number<input value={form.stockNumber} onChange={(e) => updateForm("stockNumber", e.target.value)} required maxLength={80} /></label>
          <label>Salesperson<select value={form.salespersonId} onChange={(e) => updateForm("salespersonId", e.target.value)}>{options.salespeople.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label>F&amp;I Manager<select value={form.fiManagerId} onChange={(e) => updateForm("fiManagerId", e.target.value)}>{options.fiManagers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label>Deal Status<select value={form.dealStatus} onChange={(e) => updateForm("dealStatus", e.target.value as PendingDealStatus)}>{Object.entries(dealStatusLabels).filter(([value]) => value !== "delivered_sold").map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          {visibleFinance && <label>Finance Status<select value={form.financeStatus} onChange={(e) => updateForm("financeStatus", e.target.value as PendingFinanceStatus)}>{Object.entries(financeStatusLabels).filter(([value]) => value !== "not_applicable").map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>}
          <label>{showDeliveryTime ? "Delivery Date" : "Expected Delivery Date"}<input type="date" value={form.expectedDate} onChange={(e) => updateForm("expectedDate", e.target.value)} required={showDeliveryTime} /></label>
          {showDeliveryTime && <label>Delivery Time<input type="time" value={form.deliveryTime} onChange={(e) => updateForm("deliveryTime", e.target.value)} required /></label>}
          <label>Deposit Status<select value={form.depositStatus} onChange={(e) => updateForm("depositStatus", e.target.value as PendingDepositStatus)}>{Object.entries(depositStatusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          {showDepositAmount && <label>{form.depositStatus === "refund_pending" || form.depositStatus === "refunded" ? "Refund Amount" : "Deposit Amount"}<input type="number" min="0" step="0.01" value={form.depositAmount} onChange={(e) => updateForm("depositAmount", Number(e.target.value))} /></label>}
          {showDepositDue && <label>Additional Deposit Still Due<input type="number" min="0.01" step="0.01" value={form.depositDue} onChange={(e) => updateForm("depositDue", Number(e.target.value))} required /></label>}
          <label>Next Follow-Up<input type="date" value={form.nextFollowUpDate} onChange={(e) => updateForm("nextFollowUpDate", e.target.value)} /></label>
          <label className="full-width">{closureRequested ? "Lost / Canceled Reason (required)" : selected ? "Add a Note" : "Initial Note"}<textarea value={form.note} onChange={(e) => updateForm("note", e.target.value)} required={closureRequested && (!selected || selected.dealStatus !== "lost_cancelled")} maxLength={1000} placeholder={closureRequested ? "Explain why the deal is being marked lost or canceled for Desk Manager review." : "Bank update, customer follow-up, delivery details, or anything the next manager should know."} /></label>
          {closureRequested && <div className="closure-explainer full-width"><strong>This record will stay in the active tracker.</strong><span>A Desk Manager must approve the closure before it moves to Closed History.</span></div>}
          <div className="form-actions full-width">
            <button className="primary" disabled={saving}>{saving ? "Saving…" : selected ? "Save Update" : "Add Pending Deal"}</button>
            <button type="button" className="secondary" onClick={() => setMode("list")}>Cancel</button>
          </div>
          {message && <p className="success full-width">{message}</p>}
          {error && <p className="error full-width">{error}</p>}
        </form>
      </section>
    );
  }

  if (mode === "history" && selected) {
    return (
      <section className="panel">
        <div className="panel-heading">
          <div><span className="eyebrow">Permanent activity history</span><h2>{selected.pendingId} · {selected.customer}</h2></div>
          <button type="button" className="secondary" onClick={() => setMode("list")}>Back to Tracker</button>
        </div>
        {loading ? <div className="center-panel"><div className="loader" /></div> : (
          <div className="pending-history">
            {activity.length === 0 && <p className="empty-state">No activity recorded.</p>}
            {activity.map((item) => (
              <article key={item.id} className={item.action}>
                <div><strong>{activityLabels[item.action]}</strong><span>{new Date(item.createdAt).toLocaleString()} · {item.actorEmail}</span></div>
                <p>{item.note || "Status information updated."}</p>
              </article>
            ))}
          </div>
        )}
        {error && <p className="error">{error}</p>}
      </section>
    );
  }

  if (mode === "signoff" && selected) {
    return (
      <section className="panel closure-review-panel">
        <div className="panel-heading">
          <div><span className="eyebrow">Desk Manager review</span><h2>Lost / Canceled Sign-Off</h2></div>
          <button type="button" className="secondary" onClick={() => setMode("list")}>Back to Tracker</button>
        </div>
        <div className="closure-review-summary">
          <strong>{selected.customer}</strong>
          <span>{selected.vehicle} · Stock {selected.stockNumber}</span>
          <small>{selected.pendingId} · Requested by {selected.closureRequestedByEmail || "a manager"}{selected.closureRequestedAt ? ` on ${new Date(selected.closureRequestedAt).toLocaleString()}` : ""}</small>
          <p>{selected.closureReason || selected.latestNote}</p>
        </div>
        <label>Desk Manager Note<textarea value={signoffNote} onChange={(e) => setSignoffNote(e.target.value)} maxLength={1000} placeholder="Optional when approving. Required when returning the deal to active status." /></label>
        <div className="closure-actions">
          <button type="button" className="danger-button" disabled={saving} onClick={() => void completeSignoff("approve")}>Approve Closure</button>
          <button type="button" className="secondary" disabled={saving} onClick={() => void completeSignoff("return")}>Return to Active</button>
        </div>
        {message && <p className="success">{message}</p>}
        {error && <p className="error">{error}</p>}
      </section>
    );
  }

  if (mode === "convert" && selected) {
    return (
      <section className="panel pending-convert-panel">
        <div className="panel-heading">
          <div><span className="eyebrow">Create the completed sale</span><h2>Convert {selected.pendingId}</h2></div>
          <button type="button" className="secondary" onClick={() => setMode("list")}>Back to Tracker</button>
        </div>
        <div className="pending-convert-summary"><strong>{selected.customer}</strong><span>{selected.vehicle} · Stock {selected.stockNumber}</span><small>{selected.salesperson} · {selected.fiManager}</small></div>
        <form className="deal-form" onSubmit={convert}>
          <label>Sold Date<input type="date" value={conversion.date} onChange={(e) => setConversion((current) => ({...current, date: e.target.value}))} required /></label>
          <label>Time<select value={conversion.timeSlotId} onChange={(e) => setConversion((current) => ({...current, timeSlotId: e.target.value}))}>{options.timeSlots.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
          <label>Department<select value={conversion.department} onChange={(e) => setConversion((current) => ({...current, department: e.target.value as PendingConversionInput["department"]}))}>{options.departments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label className="full-width">Type / Deal<input list="pending-convert-types" value={conversion.typeNotes} onChange={(e) => setConversion((current) => ({...current, typeNotes: e.target.value}))} placeholder="Type or choose: Retail, Fleet, FCTP, Offsite…" /><datalist id="pending-convert-types">{options.typeNotes.map((item) => <option key={item} value={item} />)}</datalist></label>
          <div className="form-actions full-width"><button className="primary" disabled={saving}>{saving ? "Converting…" : "Convert to Sold Deal"}</button><button className="secondary" type="button" onClick={() => setMode("list")}>Cancel</button></div>
          {message && <p className="success full-width">{message}</p>}
          {error && <p className="error full-width">{error}</p>}
        </form>
      </section>
    );
  }

  return (
    <section className="panel pending-tracker-panel">
      <div className="panel-heading">
        <div><span className="eyebrow">Future deals, deposits, and call-ins</span><h2>Pending Deals &amp; Deposits</h2></div>
        {canManage && <button type="button" className="primary" onClick={openAdd}>Add Pending Deal</button>}
      </div>

      <div className="delivery-quick-filters" aria-label="Delivery date views">
        <button type="button" className={scope === "today" ? "active" : ""} onClick={() => { setScope("today"); void load("today", query, customDate); }}>Today</button>
        <button type="button" className={scope === "tomorrow" ? "active" : ""} onClick={() => { setScope("tomorrow"); void load("tomorrow", query, customDate); }}>Tomorrow</button>
        <button type="button" className={scope === "next_7_days" ? "active" : ""} onClick={() => { setScope("next_7_days"); void load("next_7_days", query, customDate); }}>Next 7 Days</button>
        <button type="button" className={scope === "delivery_date" ? "active" : ""} onClick={() => { setScope("delivery_date"); void load("delivery_date", query, customDate); }}>Choose Date</button>
      </div>

      <div className={`pending-toolbar ${scope === "delivery_date" ? "with-date" : ""}`}>
        <label>Status View<select value={scope} onChange={(e) => { const next = e.target.value as PendingScope; setScope(next); void load(next, query, customDate); }}>{Object.entries(scopeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        {scope === "delivery_date" && <label>Delivery Date<input type="date" value={customDate} onChange={(e) => { setCustomDate(e.target.value); void load("delivery_date", query, e.target.value); }} /></label>}
        <label className="pending-search">Search<input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Customer, vehicle, stock, salesperson, F&I, or note" onKeyDown={(e) => { if (e.key === "Enter") void load(scope, query, customDate); }} /></label>
        <button type="button" className="secondary" onClick={() => void load(scope, query, customDate)}>Search</button>
        <button type="button" className="link-button" onClick={() => { setQuery(""); setScope("all_open"); void load("all_open", "", customDate); }}>Clear</button>
        <strong className="pending-result-count">{records.length}</strong>
      </div>

      {message && <p className="success">{message}</p>}
      {error && <p className="error">{error}</p>}
      {loading ? <div className="center-panel"><div className="loader" /><p>Loading pending deals…</p></div> : (
        <div className="pending-list">
          {records.length === 0 && <p className="empty-state">No pending deals match this view.</p>}
          {records.map((record) => {
            const isClosed = record.dealStatus === "delivered_sold" || record.closureStatus === "closed";
            const awaitingSignoff = record.closureStatus === "awaiting_signoff";
            return (
              <article className={`pending-row ${isClosed ? "closed" : ""} ${awaitingSignoff ? "awaiting-signoff" : ""}`} key={record.id}>
                <div className="pending-main">
                  <div className="pending-title"><strong>{record.customer}</strong><span>{record.pendingId}</span></div>
                  <p>{record.vehicle} · Stock {record.stockNumber}</p>
                  <small>{record.salesperson} · F&amp;I: {record.fiManager}</small>
                </div>
                <div className="pending-statuses">
                  <span className="pending-pill kind">{kindLabels[record.kind]}</span>
                  <span className={`pending-pill status ${record.dealStatus}`}>{dealStatusLabels[record.dealStatus]}</span>
                  {awaitingSignoff && <span className="pending-pill signoff">Awaiting Desk Manager Sign-Off</span>}
                  {record.kind !== "future_sale" && <span className="pending-pill finance">{financeStatusLabels[record.financeStatus]}</span>}
                </div>
                <div className="pending-dates">
                  <span><small>Expected</small>{shortDate(record.expectedDate)}{record.deliveryTime && <b>{shortTime(record.deliveryTime)}</b>}</span>
                  <span><small>Follow-Up</small>{shortDate(record.nextFollowUpDate)}</span>
                </div>
                <div className="pending-deposit">
                  <small>{depositStatusLabels[record.depositStatus]}</small>
                  {record.depositStatus !== "no_deposit" && <strong>{money(record.depositAmount)}</strong>}
                  {record.depositDue > 0 && <span>{money(record.depositDue)} still due</span>}
                </div>
                <div className="pending-note"><small>{awaitingSignoff ? "Closure Reason" : "Latest Note"}</small><p>{awaitingSignoff ? (record.closureReason || record.latestNote) : (record.latestNote || "No notes yet.")}</p></div>
                <div className="pending-row-actions">
                  {canManage && !record.linkedDealUuid && !awaitingSignoff && record.closureStatus !== "closed" && record.dealStatus !== "delivered_sold" && <button type="button" className="link-button" onClick={() => openEdit(record)}>Update</button>}
                  <button type="button" className="link-button" onClick={() => void openHistory(record)}>History</button>
                  {canSignOff && awaitingSignoff && <button type="button" className="warning-button compact" onClick={() => openSignoff(record)}>Review Sign-Off</button>}
                  {canManage && !record.linkedDealUuid && !awaitingSignoff && record.closureStatus !== "closed" && !["lost_cancelled", "delivered_sold"].includes(record.dealStatus) && <button type="button" className="primary compact" onClick={() => openConvert(record)}>Convert to Sold</button>}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {!loading && scope === "all_open" && openRecords.length > 0 && <p className="muted pending-footer-note">Every update, note, and sign-off is time-stamped and preserved in the record history.</p>}
    </section>
  );
}
